import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Shield, Mail, MapPin, Phone } from 'lucide-react';

const PrivacyPolicy = () => {
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const sections = [
    {
      id: 'interpretation',
      title: 'Interpretation and Definitions',
      content: (
        <div className="space-y-4">
          <div>
            <h4 className="font-semibold text-gray-800 mb-2">Interpretation</h4>
            <p className="text-gray-600">
              The words of which the initial letter is capitalized have meanings defined under the following conditions. 
              The following definitions shall have the same meaning regardless of whether they appear in singular or in plural.
            </p>
          </div>
          <div>
            <h4 className="font-semibold text-gray-800 mb-2">Definitions</h4>
            <p className="text-gray-600 mb-2">For the purposes of this Privacy Policy:</p>
            <ul className="list-disc pl-6 space-y-2 text-gray-600">
              <li><strong>Account</strong> means a unique account created for You to access our Service or parts of our Service.</li>
              <li><strong>Application</strong> refers to Mizpah Family Church Mobile App, the software program provided by the Church.</li>
              <li><strong>Church</strong> (referred to as either "the Church", "We", "Us" or "Our" in this Agreement) refers to Mizpah International Ministry, Ohio.</li>
              <li><strong>Country</strong> refers to: Ohio, United States</li>
              <li><strong>Device</strong> means any device that can access the Service such as a computer, a cellphone or a digital tablet.</li>
              <li><strong>Personal Data</strong> is any information that relates to an identified or identifiable individual.</li>
              <li><strong>Service</strong> refers to the Application.</li>
              <li><strong>Usage Data</strong> refers to data collected automatically, either generated by the use of the Service or from the Service infrastructure itself.</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 'collection',
      title: 'Collecting and Using Your Personal Data',
      content: (
        <div className="space-y-4">
          <div>
            <h4 className="font-semibold text-gray-800 mb-2">Types of Data Collected</h4>
            
            <h5 className="font-semibold text-gray-700 mt-4 mb-2">Personal Data</h5>
            <p className="text-gray-600 mb-2">
              While using Our Service, We may ask You to provide Us with certain personally identifiable information 
              that can be used to contact or identify You. Personally identifiable information may include, but is not limited to:
            </p>
            <ul className="list-disc pl-6 space-y-1 text-gray-600">
              <li>Email address</li>
              <li>First name and last name</li>
              <li>Phone number</li>
              <li>Prayer requests and testimonies</li>
              <li>Event registration information</li>
              <li>Donation history</li>
              <li>Usage Data</li>
            </ul>

            <h5 className="font-semibold text-gray-700 mt-4 mb-2">Usage Data</h5>
            <p className="text-gray-600 mb-2">
              Usage Data is collected automatically when using the Service. This may include information such as 
              Your Device's Internet Protocol address, browser type, the pages of our Service that You visit, 
              the time and date of Your visit, the time spent on those pages, and other diagnostic data.
            </p>

            <h5 className="font-semibold text-gray-700 mt-4 mb-2">Information Collected While Using the Application</h5>
            <p className="text-gray-600 mb-2">
              While using Our Application, in order to provide features of Our Application, We may collect, 
              with Your prior permission:
            </p>
            <ul className="list-disc pl-6 space-y-1 text-gray-600">
              <li>Information regarding your location (for church events and directions)</li>
              <li>Pictures and other information from your Device's camera and photo library</li>
              <li>Calendar information (for event scheduling)</li>
              <li>Notifications preferences</li>
            </ul>
            <p className="text-gray-600 mt-2">
              You can enable or disable access to this information at any time through Your Device settings.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'use',
      title: 'Use of Your Personal Data',
      content: (
        <div className="space-y-3">
          <p className="text-gray-600">The Church may use Personal Data for the following purposes:</p>
          <ul className="list-disc pl-6 space-y-2 text-gray-600">
            <li><strong>To provide and maintain our Service</strong>, including to monitor the usage of our Service.</li>
            <li><strong>To manage Your Account</strong>: to manage Your registration as a user of the Service and give You access to different functionalities.</li>
            <li><strong>For spiritual support</strong>: to respond to prayer requests, provide pastoral care, and deliver spiritual content.</li>
            <li><strong>To contact You</strong>: by email, telephone calls, SMS, push notifications regarding updates, events, services, and important church announcements.</li>
            <li><strong>To provide You</strong> with news, special offers, events, and general information about church activities, services, and ministries.</li>
            <li><strong>To manage Your requests</strong>: to attend and manage Your requests to Us.</li>
            <li><strong>For event management</strong>: to organize and communicate about church events, conferences, and gatherings.</li>
            <li><strong>For donation processing</strong>: to process and acknowledge donations and offerings (if applicable).</li>
          </ul>
        </div>
      )
    },
    {
      id: 'sharing',
      title: 'Sharing of Your Personal Information',
      content: (
        <div className="space-y-3">
          <p className="text-gray-600">We may share Your personal information in the following situations:</p>
          <ul className="list-disc pl-6 space-y-2 text-gray-600">
            <li><strong>With Service Providers</strong>: to facilitate our Service, provide the Service on behalf of the Church, or assist in analyzing how our Service is used.</li>
            <li><strong>With Ministry Partners</strong>: for collaborative ministry efforts and events.</li>
            <li><strong>Within the Church</strong>: with church leaders and staff for pastoral care and ministry purposes.</li>
            <li><strong>With Your consent</strong>: We may disclose Your personal information for any other purpose with Your consent.</li>
          </ul>
          <p className="text-gray-600 mt-3">
            <strong>Note:</strong> We will never sell Your personal information to third parties. Your data is used solely for ministry and church purposes.
          </p>
        </div>
      )
    },
    {
      id: 'retention',
      title: 'Retention and Security of Your Personal Data',
      content: (
        <div className="space-y-4">
          <div>
            <h4 className="font-semibold text-gray-800 mb-2">Data Retention</h4>
            <p className="text-gray-600">
              The Church will retain Your Personal Data only for as long as is necessary for the purposes set out in this Privacy Policy. 
              We will retain and use Your Personal Data to comply with our legal obligations, resolve disputes, and maintain accurate church records.
            </p>
          </div>
          <div>
            <h4 className="font-semibold text-gray-800 mb-2">Security</h4>
            <p className="text-gray-600">
              The security of Your Personal Data is important to Us. We strive to use commercially acceptable means to protect Your Personal Data, 
              including encryption and secure servers. However, no method of transmission over the Internet or electronic storage is 100% secure.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'rights',
      title: 'Your Privacy Rights',
      content: (
        <div className="space-y-3">
          <p className="text-gray-600">You have the right to:</p>
          <ul className="list-disc pl-6 space-y-2 text-gray-600">
            <li><strong>Access</strong> Your Personal Data we hold about You</li>
            <li><strong>Correct</strong> any inaccurate or incomplete data</li>
            <li><strong>Delete</strong> Your Personal Data (subject to certain legal obligations)</li>
            <li><strong>Opt-out</strong> of marketing communications at any time</li>
            <li><strong>Withdraw consent</strong> for data processing where consent is the legal basis</li>
          </ul>
          <p className="text-gray-600 mt-3">
            You may update, amend, or delete Your information at any time by signing in to Your Account and visiting 
            the account settings section, or by contacting Us directly.
          </p>
        </div>
      )
    },
    {
      id: 'children',
      title: "Children's Privacy",
      content: (
        <div className="space-y-3">
          <p className="text-gray-600">
            Our Service does not address anyone under the age of 13. We do not knowingly collect personally identifiable 
            information from anyone under the age of 13. If You are a parent or guardian and You are aware that Your child 
            has provided Us with Personal Data, please contact Us.
          </p>
          <p className="text-gray-600">
            If We become aware that We have collected Personal Data from anyone under the age of 13 without verification 
            of parental consent, We take steps to remove that information from Our servers.
          </p>
        </div>
      )
    },
    {
      id: 'links',
      title: 'Links to Other Websites',
      content: (
        <div className="space-y-3">
          <p className="text-gray-600">
            Our Service may contain links to other websites that are not operated by Us, including social media platforms 
            and ministry partner sites. If You click on a third party link, You will be directed to that third party's site.
          </p>
          <p className="text-gray-600">
            We strongly advise You to review the Privacy Policy of every site You visit. We have no control over and assume 
            no responsibility for the content, privacy policies or practices of any third party sites or services.
          </p>
        </div>
      )
    },
    {
      id: 'changes',
      title: 'Changes to This Privacy Policy',
      content: (
        <div className="space-y-3">
          <p className="text-gray-600">
            We may update Our Privacy Policy from time to time. We will notify You of any changes by posting the new 
            Privacy Policy on this page and updating the "Last updated" date.
          </p>
          <p className="text-gray-600">
            We will let You know via email and/or a prominent notice on Our Service, prior to the change becoming effective. 
            You are advised to review this Privacy Policy periodically for any changes.
          </p>
        </div>
      )
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <div className="max-w-4xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-6">
          <div className="flex items-center justify-center mb-4">
            <Shield className="w-12 h-12 text-blue-600" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-center text-gray-900 mb-2">
            Privacy Policy
          </h1>
          <h2 className="text-xl text-center text-blue-600 font-semibold mb-4">
            Mizpah International Ministry
          </h2>
          <p className="text-center text-gray-600 mb-6">
            A Faith-Filled Church in Ohio
          </p>
          <div className="text-center text-sm text-gray-500">
            Last updated: November 22, 2025
          </div>
        </div>

        {/* Introduction */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <p className="text-gray-700 leading-relaxed">
            This Privacy Policy describes Our policies and procedures on the collection, use, and disclosure of Your 
            information when You use the Mizpah Family Church Mobile Application and tells You about Your privacy rights 
            and how the law protects You.
          </p>
          <p className="text-gray-700 leading-relaxed mt-4">
            We use Your Personal data to provide and improve the Service, support Your spiritual journey, and keep You 
            connected with our church community. By using the Service, You agree to the collection and use of information 
            in accordance with this Privacy Policy.
          </p>
        </div>

        {/* Expandable Sections */}
        <div className="space-y-4">
          {sections.map((section) => (
            <div key={section.id} className="bg-white rounded-xl shadow-md overflow-hidden">
              <button
                onClick={() => toggleSection(section.id)}
                className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <h3 className="text-lg font-semibold text-gray-900 text-left">
                  {section.title}
                </h3>
                {expandedSections[section.id] ? (
                  <ChevronUp className="w-5 h-5 text-gray-500 flex-shrink-0" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
                )}
              </button>
              {expandedSections[section.id] && (
                <div className="px-6 pb-6 border-t border-gray-100">
                  <div className="pt-4">
                    {section.content}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Contact Information */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl shadow-lg p-8 mt-8 text-white">
          <h3 className="text-2xl font-bold mb-6 text-center">Contact Us</h3>
          <p className="text-center mb-6 text-blue-50">
            If you have any questions about this Privacy Policy, You can contact us:
          </p>
          
          <div className="space-y-4 max-w-md mx-auto">
            <div className="flex items-start space-x-3 bg-white/10 rounded-lg p-4 backdrop-blur-sm">
              <Mail className="w-5 h-5 flex-shrink-0 mt-1" />
              <div>
                <p className="font-semibold">Email</p>
                <a href="mailto:info@mizpahfamily.com" className="text-blue-100 hover:text-white transition-colors">
                  info@mizpahfamily.com
                </a>
              </div>
            </div>
            
            <div className="flex items-start space-x-3 bg-white/10 rounded-lg p-4 backdrop-blur-sm">
              <MapPin className="w-5 h-5 flex-shrink-0 mt-1" />
              <div>
                <p className="font-semibold">Location</p>
                <p className="text-blue-100">Ohio, United States</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-3 bg-white/10 rounded-lg p-4 backdrop-blur-sm">
              <Phone className="w-5 h-5 flex-shrink-0 mt-1" />
              <div>
                <p className="font-semibold">Website</p>
                <a href="https://mizpahfamily.com" target="_blank" rel="noopener noreferrer" 
                   className="text-blue-100 hover:text-white transition-colors">
                  mizpahfamily.com
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-gray-500">
          <p>© 2025 Mizpah International Ministry. All rights reserved.</p>
          <p className="mt-2">Where prayers are lifted daily, and faith is strengthened.</p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;